import { List, Map, OrderedMap } from 'immutable'
import { combineReducers } from 'redux-immutable'
import { combineActions, handleActions } from 'redux-actions'
import { TEMP_ID_KEY_PATH } from './models'
import * as actions from './actions'
import { MessageStatus } from './constants'
import attachment from './attachment'

const paginatedStatus = handleActions({
  [combineActions(
    actions.fetchPaginatedConversationMessagesPending,
    actions.fetchPaginatedConversationMessagesFailed,
    actions.receivePaginatedMessageList,
  )]: (state, action) => {
    const { conversationId } = action.payload
    return state.setIn([conversationId, 'firstFetch'], action.type)
  },
  [combineActions(
    actions.fetchNextPaginatedConversationMessagesPending,
    actions.fetchNextPaginatedConversationMessagesFailed,
    actions.receiveNextMessageList,
  )]: (state, action) => {
    const { conversationId } = action.payload
    return state.setIn([conversationId, 'paginateFetch'], action.type)
  }
}, Map())

const readReceipts = handleActions({
  [combineActions(
    actions.receivePaginatedMessageList,
    actions.receiveNextMessageList,
  )]: (state, action) => {
    const { messages } = action.payload
    return state.withMutations(_state => {
      messages.forEach(message => {
        _state.set(message.id, message.recipientStatuses)
      })
    })
  },

  [actions.receiveMessageRead]: (state, action) => {
    const { messageId, recipientType, recipientId, status } = action.payload
    return state.setIn([messageId, recipientType, recipientId], status)
  },

  [actions.messageReadSuccess]: (state, action) => {
    const { messageId, recipientType, recipientId } = action.payload
    return state.setIn([messageId, recipientType, recipientId], MessageStatus.Read)
  },

  [actions.bulkMessageReadSuccess]: (state, action) => {
    const { messages, recipientType, recipientId } = action.payload
    return messages.reduce((acc, { messageId }) => state.setIn([messageId, recipientType, recipientId], MessageStatus.Read), state)
  },
}, Map())

const paginatedConversationMessages = handleActions({
  [actions.createMessagePending]: (state, action) => {
    const { conversationId, message } = action.payload
    if (message.type === 'EDIT') {
      return state.setIn(['editMessagesById', conversationId, message.getIn(TEMP_ID_KEY_PATH)], message)
    } else {
      return state.updateIn(
        ['messagesByConversationId', conversationId],
        messages => messages.push(message.set('id', message.getIn(TEMP_ID_KEY_PATH)))
      )
    }
  },

  [actions.receiveMessage]: (state, action) => {
    const { conversationId, message } = action.payload
    if (message.type === 'EDIT' && state.hasIn(['editMessagesById', conversationId])) {
      return state
        .deleteIn(['editMessagesById', conversationId, message.getIn(TEMP_ID_KEY_PATH)])
        .setIn(['editMessagesById', conversationId, message.id], message)
    } else if (state.hasIn(['messagesByConversationId', conversationId])) {
      return state.updateIn(
        ['messagesByConversationId', conversationId],
        messages => {
          const removeTemp = messages.filter(({ id }) => id !== message.getIn(TEMP_ID_KEY_PATH) && id !== message.id)
          const addIndex = removeTemp.findIndex(({ createdAt }) => createdAt > message.createdAt)
          if (addIndex === -1) {
            return removeTemp.push(message)
          } else {
            return removeTemp.insert(addIndex, message)
          }
        }
      )
    } else {
      return state
    }
  },

  [actions.receiveBulkUpdateMessages]: (state, action) => {
    const { messages } = action.payload

    return state.withMutations(_state => {
      messages.forEach(message => {
        if (_state.hasIn(['messagesByConversationId', message.conversationId])) {
          _state.updateIn(
            ['messagesByConversationId', message.conversationId],
            (existingMessages) => {
              const removeTemp = existingMessages.filter(({ id }) => id !== message.getIn(TEMP_ID_KEY_PATH) && id !== message.id)
              const addIndex = removeTemp.findIndex(({ createdAt }) => createdAt > message.createdAt)
              if (addIndex === -1) {
                return removeTemp.push(message)
              } else {
                return removeTemp.insert(addIndex, message)
              }
            }
          )
        }
      })
    })
  },

  [actions.createMessageFailed]: (state, action) => {
    const { conversationId, message } = action.payload
    if (state.hasIn('editMessagesById', conversationId, message.getIn(TEMP_ID_KEY_PATH))) {
      return state.setIn(['editMessagesById', conversationId, message.getIn(TEMP_ID_KEY_PATH), 'failed'], true)
    } else {
      return state.updateIn(['messagesByConversationId', conversationId], messages => {
        const index = messages.findIndex(m => m.id === message.getIn(TEMP_ID_KEY_PATH))
        return messages.setIn([index, 'failed'], true)
      })
    }
    return state
  },

  [actions.receivePaginatedMessageList]: (state, action) => {
    const { messages, conversationId, more, next } = action.payload
    const messagesGroupedByIfIsEditMessages = messages.groupBy(message => message.get('type') === 'EDIT')
    const editMessages = messagesGroupedByIfIsEditMessages.get(true, OrderedMap())
    const nonEditMessages = messagesGroupedByIfIsEditMessages.get(false, OrderedMap())
    return state
      .setIn(['editMessagesById', conversationId], editMessages)
      .setIn(['paginationById', conversationId, 'more'], more)
      .setIn(['paginationById', conversationId, 'next'], next)
      .setIn(['messagesByConversationId', conversationId], nonEditMessages.toList())
  },

  [actions.receiveNextMessageList]: (state, action) => {
    const { messages, conversationId, more, next } = action.payload
    const messagesGroupedByIfIsEditMessages = messages.groupBy(message => message.get('type') === 'EDIT')
    const editMessages = messagesGroupedByIfIsEditMessages.get(true, OrderedMap())
    const nonEditMessages = messagesGroupedByIfIsEditMessages.get(false, OrderedMap())
    return state
      .updateIn(['messagesByConversationId', conversationId], existingMessages => nonEditMessages.toList().concat(existingMessages))
      .updateIn(['editMessagesById', conversationId], existingMessages => existingMessages.merge(editMessages))
      .setIn(['paginationById', conversationId, 'more'], more)
      .setIn(['paginationById', conversationId, 'next'], next)
  },

  [actions.removeMessage]: (state, action) => {
    const { conversationId, id } = action.payload
    return state
      .deleteIn(['editMessagesById', conversationId, id])
      .updateIn(
        ['messagesByConversationId', conversationId],
        messages => messages.filter(message => id !== message.id)
      )
  },

  [actions.receiveFirstMessages]: (state, action) => {
    const { messages, conversationId } = action.payload
    return state.setIn(['firstMessagesById', conversationId], messages)
  },

}, Map({
  messagesByConversationId: Map(),
  firstMessagesById: Map(),
  paginationById: Map(),
  editMessagesById: Map()
}))

const firstMessagesStatus = handleActions({
  [combineActions(
    actions.fetchFirstMessagesPending,
    actions.fetchFirstMessagesFailed,
    actions.receiveFirstMessages,
  )]: (state, action) => {
    const { conversationId } = action.payload

    return state.set(conversationId, action.type)
  }
}, Map())

export default combineReducers({
  paginatedConversationMessages,
  firstMessagesStatus,
  paginatedStatus,
  readReceipts,
  attachment,
})



// WEBPACK FOOTER //
// ./src/scripts/app/modules/message/reducer.js